// src/pages/UserManagement/modals/EditUserModal.tsx
// Edit user modal (no password fields)

import React, { useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { User, Department, Role } from '../types';
import { VN_MOBILE_PHONE_REGEX, VN_MOBILE_PHONE_ERROR_MESSAGE } from 'helpers/phoneValidation';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('user_management');

interface EditUserModalProps {
  isOpen: boolean;
  toggle: () => void;
  user: User | null;
  departments: Department[];
  roles: Role[];
  onSubmit: (values: any) => Promise<void>;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  toggle,
  user,
  departments,
  roles,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: user?.name || '',
      email: user?.email || '',
      phoneNumber: user?.phoneNumber || '',
      departmentId: user?.departmentId?.toString() || '',
      roleId: user?.roleId?.toString() || '',
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required(st('validation_name_required'))
        .min(2, st('validation_name_min'))
        .max(255, st('validation_name_max')),
      email: Yup.string()
        .email(st('validation_email_format'))
        .required(st('validation_email_required')),
      phoneNumber: Yup.string()
        .required(st('validation_phone_required'))
        .matches(VN_MOBILE_PHONE_REGEX, VN_MOBILE_PHONE_ERROR_MESSAGE),
      roleId: Yup.string().required(st('validation_role_required')),
    }),
    onSubmit: async (values) => {
      await onSubmit({ ...values, id: user?.id });
    },
  });

  const handleClose = () => {
    validation.resetForm();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        {st('chinh_sua_nguoi_dung_title')}
      </ModalHeader>
      <Form onSubmit={validation.handleSubmit}>
        <ModalBody>
          <Row>
            {/* Name */}
            <Col md={12}>
              <FormGroup>
                <Label for="name">{st('name_label')} <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  placeholder={st('name_placeholder')}
                  maxLength={255}
                  value={validation.values.name}
                  data-action-code="edit-input-name"
                  data-action-name="Tên người dùng (Sửa)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-name")}
                  invalid={
                    validation.touched.name && validation.errors.name
                      ? true
                      : false
                  }
                />
                <div className="text-end">
                  <small className="text-muted">{validation.values.name.length}/255</small>
                </div>
                {validation.touched.name && validation.errors.name ? (
                  <FormFeedback type="invalid">
                    {validation.errors.name}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Email */}
            <Col md={6}>
              <FormGroup>
                <Label for="email">{st('email_label')} <span className="text-danger">*</span></Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={st('email_placeholder')}
                  value={validation.values.email}
                  data-action-code="edit-input-email"
                  data-action-name="Email (Sửa)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-email")}
                  invalid={
                    validation.touched.email && validation.errors.email
                      ? true
                      : false
                  }
                />
                {validation.touched.email && validation.errors.email ? (
                  <FormFeedback type="invalid">
                    {validation.errors.email}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Phone Number */}
            <Col md={6}>
              <FormGroup>
                <Label for="phoneNumber">{st('phone_number_label')} <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="phoneNumber"
                  name="phoneNumber"
                  placeholder={st('phone_number_placeholder')}
                  value={validation.values.phoneNumber}
                  data-action-code="edit-input-phone-number"
                  data-action-name="Số điện thoại (Sửa)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-phone-number")}
                  invalid={
                    validation.touched.phoneNumber &&
                    validation.errors.phoneNumber
                      ? true
                      : false
                  }
                />
                {validation.touched.phoneNumber &&
                validation.errors.phoneNumber ? (
                  <FormFeedback type="invalid">
                    {validation.errors.phoneNumber}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Department */}
            <Col md={6}>
              <FormGroup>
                <Label for="departmentId">{st('department_label')}</Label>
                <Input
                  type="select"
                  id="departmentId"
                  name="departmentId"
                  value={validation.values.departmentId}
                  data-action-code="edit-input-department"
                  data-action-name="Phòng ban (Sửa)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-department")}
                >
                  <option value="">{st('department_placeholder')}</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            {/* Role */}
            <Col md={6}>
              <FormGroup>
                <Label for="roleId">{st('role_label')} <span className="text-danger">*</span></Label>
                <Input
                  type="select"
                  id="roleId"
                  name="roleId"
                  value={validation.values.roleId}
                  data-action-code="edit-input-role"
                  data-action-name="Vai trò (Sửa)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-role")}
                  invalid={
                    validation.touched.roleId && validation.errors.roleId
                      ? true
                      : false
                  }
                >
                  <option value="">{st('role_placeholder')}</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </Input>
                {validation.touched.roleId && validation.errors.roleId ? (
                  <FormFeedback type="invalid">
                    {validation.errors.roleId}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
          </Row>

          {/* Info box */}
          <div className="alert alert-info mb-0" role="alert">
            <i className="ri-information-line me-2"></i>
            {st('de_thay_doi_mat_khau_vui_text')}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleClose}>
            {st('huy_button')}
          </Button>
          {PageActionPermission.canDisplay(location.pathname, "button-edit-submit") && (
            <Button
              color="primary"
              type="submit"
              disabled={validation.isSubmitting}
              data-action-code="button-edit-submit"
              data-action-name="Lưu thay đổi người dùng"
            >
              <i className="ri-save-line me-1"></i>
              {validation.isSubmitting ? st('dang_luu_text') : st('luu_text')}
            </Button>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default EditUserModal;
